import { Button } from 'lib/components';
import { TextField, TimezoneSelect } from 'lib/components/FormFields';
import React, { useCallback, useEffect, useState } from 'react';
import { useCustomToast } from '../../hooks';
import { routes } from '../../lib/api';
import { actions, RequestManager, useAppDispatch, useSelectRequest, useSelectSettings } from '../../store';
import classes from './index.module.scss';

/**
 * Props interface for the GeneralTab component
 * Currently empty as the component doesn't require any props
 */
interface Props {}

/**
 * GeneralTab component
 * Displays and manages general system settings like tax amount and timezone
 * Allows super admins to update these global settings
 *
 * @returns {JSX.Element} - Rendered component
 */
const GeneralTab = ({}: Props) => {
	// Custom toast notifications for user feedback
	const toast = useCustomToast();

	// Form state variables
	/** Tax amount value - displayed with $ symbol */
	const [taxAmount, setTaxAmount] = useState(0);
	/** Global timezone setting with default value */
	const [globalTimezone, setGlobalTimezone] = useState<string | undefined>('UTC-06:00 Central Time (US & Canada)');

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Active API request tracking
	const { active } = useSelectRequest();
	// Settings data from Redux store
	const settings = useSelectSettings();

	/**
	 * Monitor API requests for settings updates
	 * Shows toast notifications for success/failure
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.SETTINGS, 'PUT')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
			}
		}
	}, [active]);

	/**
	 * Initialize form values from settings data when component mounts
	 * Parses tax amount as a float and sets timezone from stored settings
	 */
	useEffect(() => {
		for (const setting of settings.list) {
			if (setting.name === 'tax_amount') {
				setTaxAmount(parseFloat(setting.value));
				continue;
			}

			if (setting.name === 'global_timezone') {
				setGlobalTimezone(setting.value);
				continue;
			}
		}
	}, []);

	/**
	 * Handle settings update form submission
	 * Dispatches updateSettings action with current form values
	 * Memoized with useCallback to prevent unnecessary re-renders
	 */
	const handleUpdate = useCallback(() => {
		dispatch(
			actions.updateSettings([
				{
					name: 'tax_amount',
					value: taxAmount.toFixed(2), // Format tax amount to 2 decimal places
				},
				{
					name: 'global_timezone',
					value: globalTimezone || 'UTC-06:00 Central Time (US & Canada)', // Use default if not set
				},
			])
		);
	}, [dispatch, taxAmount, globalTimezone]);

	/**
	 * Log timezone changes to console for debugging
	 * This could be removed in production
	 */
	useEffect(() => {
		console.log('Global Timezone: ', globalTimezone);
	}, [globalTimezone]);

	return (
		<div className={classes['generalTab']}>
			{/* Settings form with input fields */}
			<div className={classes['settings-form']}>
				{/* Tax amount input field with $ symbol */}
				<TextField
					variant="labelled"
					label="Tax Amount: "
					placeholder="9.00"
					value={taxAmount.toLocaleString()}
					onChange={(e) => setTaxAmount(+e.target.value)}
					numberSymbol={['$']}
				/>
				{/* Spacer between form fields */}
				<div style={{ width: 16 }} />
				{/* Timezone selection dropdown */}
				<TimezoneSelect label={'System Timezone:'} value={globalTimezone} setValue={setGlobalTimezone} />
			</div>
			{/* Form action buttons */}
			<div className={classes['footButtons']}>
				{/* Update button to save settings */}
				<Button variant="standard" title="Update" onClick={handleUpdate} />
			</div>
		</div>
	);
};

export default GeneralTab;
